<template>
  <div class="recharge-page">
    <header class="recharge-hero">
      <div>
        <span class="recharge-kicker">BALANCE RECHARGE</span>
        <h1>{{ text('在线充值余额', 'Recharge Balance') }}</h1>
        <p>{{ text('选择金额与支付方式，支付成功后余额将自动入账。', 'Choose an amount and payment method. Your balance is credited after payment.') }}</p>
      </div>
      <div class="balance-card">
        <span>{{ text('当前余额', 'Current balance') }}</span>
        <strong>{{ balance.toFixed(2) }}U</strong>
        <a-button type="link" icon="reload" :loading="balanceLoading" @click="loadBalance">{{ text('刷新余额', 'Refresh balance') }}</a-button>
      </div>
    </header>

    <div class="recharge-grid">
      <section class="recharge-card">
        <div class="card-heading">
          <div>
            <h2>{{ text('选择充值金额', 'Choose an amount') }}</h2>
            <p>{{ text('选择快捷金额，或输入自定义金额。', 'Select a preset or enter a custom amount.') }}</p>
          </div>
          <a-icon type="wallet" />
        </div>
        <div class="amount-presets">
          <button v-for="value in presetAmounts" :key="value" type="button" :class="{ selected: amount === value }" @click="amount = value">
            <span>$</span>{{ value }}
          </button>
        </div>
        <label class="amount-label" for="recharge-amount">{{ text('自定义充值金额', 'Custom recharge amount') }}</label>
        <a-input-number id="recharge-amount" v-model="amount" :min="0.01" :max="500" :precision="2" :step="0.01" size="large" style="width: 100%" />
        <p class="amount-help">{{ text('单次最多充值 500U，最多保留两位小数。USDT 通道仅支持整数金额。', 'Maximum 500U per recharge, with up to two decimal places. USDT channels accept whole amounts only.') }}</p>
      </section>

      <section class="recharge-card summary-card">
        <div class="card-heading">
          <div>
            <h2>{{ text('充值确认', 'Recharge summary') }}</h2>
            <p>{{ text('确认金额后选择支付通道。', 'Confirm the amount and choose a payment method.') }}</p>
          </div>
          <a-icon type="safety-certificate" />
        </div>
        <div class="summary-rows">
          <div><span>{{ text('充值金额', 'Recharge amount') }}</span><strong>${{ formattedAmount }}</strong></div>
          <div><span>{{ text('预计到账余额', 'Expected balance credit') }}</span><strong>+{{ formattedAmount }}U</strong></div>
          <div><span>{{ text('入账方式', 'Credit method') }}</span><strong>{{ text('支付成功后自动入账', 'Automatic after payment') }}</strong></div>
        </div>
        <div class="payment-heading">
          <h3>{{ text('选择支付方式', 'Choose a payment method') }}</h3>
          <span>{{ text('支付通道由后台实时提供', 'Payment methods are loaded from the server') }}</span>
        </div>
        <div v-if="channelsLoading" class="channel-state"><a-spin /> {{ text('正在获取支付通道…', 'Loading payment methods…') }}</div>
        <div v-else-if="!channels.length" class="channel-state">
          {{ text('暂无可用支付通道', 'No payment methods available') }}
          <a-button icon="reload" @click="loadChannels">{{ text('重试', 'Retry') }}</a-button>
        </div>
        <div v-else class="payment-actions">
          <a-button v-for="channel in channels" :key="channel.dictValue" type="primary" size="large" :loading="orderLoading && activeChannel === channel.dictValue" :disabled="orderLoading || !validAmount" @click="createOrder(channel)">{{ channel.dictLabel }}</a-button>
        </div>
        <p v-if="!validAmount" class="amount-error">{{ text('请输入大于 0 且不超过 500U 的金额，最多两位小数。', 'Enter an amount above zero and up to 500U, with no more than two decimals.') }}</p>
        <div class="safety-note"><a-icon type="check-circle" />{{ text('支付成功后系统会自动更新余额，无需上传凭证。', 'Your balance updates automatically after successful payment.') }}</div>
      </section>
    </div>

    <a-modal :visible="payOpen" :title="text('余额充值安全支付', 'Secure balance recharge')" :footer="null" :width="900" :body-style="{ padding: '16px' }" @cancel="closePayment">
      <div class="payment-frame"><a-spin :spinning="payFrameLoading"><iframe v-if="payUrl" :src="payUrl" :title="text('充值支付页面', 'Recharge checkout')" @load="payFrameLoading = false" /></a-spin></div>
      <p class="modal-note">{{ text('请在支付页面完成付款。关闭窗口后可刷新查看余额。', 'Complete payment in the checkout page. Close this window to refresh your balance.') }}</p>
      <a v-if="payUrl" :href="payUrl" target="_blank" rel="noopener noreferrer">{{ text('支付页面无法显示？在新窗口打开', 'Checkout not visible? Open in a new window') }}</a>
    </a-modal>
  </div>
</template>

<script>
export default {
  name: 'DashboardRecharge',
  inject: ['languageState'],
  data() {
    return {
      presetAmounts: [20, 50, 100, 200, 300, 500],
      amount: 100,
      balance: 0,
      balanceLoading: false,
      channels: [],
      channelsLoading: false,
      orderLoading: false,
      activeChannel: null,
      payOpen: false,
      payUrl: '',
      payFrameLoading: false
    };
  },
  computed: {
    validAmount() {
      const value = Number(this.amount);
      return this.amount !== null && Number.isFinite(value) && value > 0 && value <= 500 && Math.abs(value * 100 - Math.round(value * 100)) < 1e-8;
    },
    formattedAmount() {
      return this.validAmount ? Number(this.amount).toFixed(2) : '0.00';
    }
  },
  mounted() {
    this.loadBalance();
    this.loadChannels();
  },
  methods: {
    text(zh, en) {
      return this.languageState.languageCode === 'zh' ? zh : en;
    },
    responseBody(response) {
      return response && response.code !== undefined ? response : (response && response.data) || {};
    },
    async loadBalance() {
      this.balanceLoading = true;
      try {
        const body = this.responseBody(await this.$request.get('/balance/getBalance'));
        if (body.code === 200 && body.data) {
          const value = Number(body.data.balance);
          this.balance = Number.isFinite(value) ? value : 0;
        } else {
          this.$message.warning(body.msg || this.text('余额加载失败', 'Unable to load balance'));
        }
      } catch (error) {
        this.$message.error(this.text('余额加载失败，请重试', 'Unable to load balance. Please retry.'));
      } finally {
        this.balanceLoading = false;
      }
    },
    async loadChannels() {
      this.channelsLoading = true;
      try {
        const body = this.responseBody(await this.$request.get('/package/getpassagewaytype'));
        if (body.code === 200 && Array.isArray(body.data)) {
          this.channels = body.data.filter(item => item && item.dictValue !== undefined && item.dictValue !== null && item.dictValue !== '');
        } else {
          this.channels = [];
          this.$message.warning(body.msg || this.text('支付通道加载失败', 'Unable to load payment methods'));
        }
      } catch (error) {
        this.channels = [];
        this.$message.error(this.text('支付通道加载失败，请重试', 'Unable to load payment methods. Please retry.'));
      } finally {
        this.channelsLoading = false;
      }
    },
    async createOrder(channel) {
      if (this.orderLoading || !this.validAmount) return;
      const passagewayType = String(channel.dictValue);
      if (`${channel.dictLabel || ''} ${passagewayType}`.toLowerCase().includes('usdt') && !Number.isInteger(Number(this.amount))) {
        this.$message.warning(this.text('USDT 通道仅支持整数金额', 'USDT methods accept whole amounts only'));
        return;
      }
      this.orderLoading = true;
      this.activeChannel = channel.dictValue;
      try {
        const body = this.responseBody(await this.$request.post('/order/rechargeBalance', {
          amount: Number(this.amount),
          passagewayType
        }));
        if (body.code !== 200) {
          this.$message.warning(body.msg || this.text('充值订单创建失败', 'Unable to create recharge order'));
          return;
        }
        const url = String((body.data && body.data.paylink) || '').trim();
        if (!/^https?:\/\//i.test(url)) {
          this.$message.warning(this.text('支付链接无效', 'Invalid payment URL'));
          return;
        }
        this.payUrl = url;
        this.payFrameLoading = true;
        this.payOpen = true;
      } catch (error) {
        this.$message.error(this.text('充值订单创建失败，请重试', 'Unable to create recharge order. Please retry.'));
      } finally {
        this.orderLoading = false;
        this.activeChannel = null;
      }
    },
    closePayment() {
      this.payOpen = false;
      this.payUrl = '';
      this.payFrameLoading = false;
      this.loadBalance();
    }
  }
};
</script>

<style scoped>
.recharge-page{max-width:1280px;margin:0 auto;padding:28px 24px 60px;color:#24324b}.recharge-hero{display:flex;justify-content:space-between;align-items:center;gap:24px;margin-bottom:22px;padding:30px 34px;border-radius:18px;background:linear-gradient(120deg,#142a50,#205ca4);color:#fff}.recharge-kicker{font-size:11px;font-weight:700;letter-spacing:.18em;color:#a9d8ff}.recharge-hero h1{margin:8px 0;color:#fff;font-size:30px;line-height:1.25}.recharge-hero p{margin:0;color:#d9e9ff;line-height:1.65}.balance-card{display:grid;gap:5px;min-width:235px;padding:18px 22px;border:1px solid #cfe4ff;border-radius:14px;background:#fff;color:#687c98}.balance-card strong{color:#1760aa;font-size:27px}.balance-card .ant-btn{padding:0;text-align:left}.recharge-grid{display:grid;grid-template-columns:minmax(0,1.1fr) minmax(340px,.9fr);gap:20px;align-items:start}.recharge-card{padding:26px;border:1px solid #e1e8f2;border-radius:16px;background:#fff;box-shadow:0 8px 25px rgba(24,54,92,.05)}.card-heading{display:flex;justify-content:space-between;gap:15px;align-items:flex-start;margin-bottom:22px}.card-heading h2{margin:0 0 5px;font-size:19px;color:#1c3355}.card-heading p{margin:0;color:#8392a7;font-size:13px}.card-heading>.anticon{color:#3182ce;font-size:27px}.amount-presets{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-bottom:25px}.amount-presets button{height:76px;border:1px solid #d7e2f0;border-radius:11px;background:#fff;color:#1d3558;font-size:24px;font-weight:700;cursor:pointer}.amount-presets button span{margin-right:3px;color:#3382cb;font-size:17px}.amount-presets button:hover,.amount-presets button.selected{border-color:#3182ce;background:#f0f7ff;color:#115697}.amount-presets button:focus-visible{outline:2px solid #3182ce;outline-offset:2px}.amount-label{display:block;margin-bottom:9px;color:#3f536d;font-weight:600}.amount-help{margin:9px 0 0;color:#8593a7;font-size:12px;line-height:1.6}.summary-rows{overflow:hidden;border:1px solid #e0e8f2;border-radius:10px}.summary-rows>div{display:flex;justify-content:space-between;gap:12px;padding:15px;border-bottom:1px solid #e7edf5}.summary-rows>div:last-child{border-bottom:0;background:#f1f7ff}.summary-rows span{color:#7e8fa6}.summary-rows strong{text-align:right;color:#1c3559}.summary-rows>div:first-child strong{color:#1768b5;font-size:21px}.payment-heading{margin-top:22px}.payment-heading h3{margin:0;color:#1c3559;font-size:15px}.payment-heading span{font-size:12px;color:#8a99ad}.payment-actions{display:flex;flex-wrap:wrap;gap:10px;margin-top:14px}.payment-actions .ant-btn{min-width:112px}.channel-state{display:flex;align-items:center;gap:12px;min-height:50px;color:#7c8ba0}.amount-error{margin:9px 0 0;color:#d94b4b;font-size:12px}.safety-note{display:flex;gap:9px;align-items:flex-start;margin-top:20px;padding:13px;border:1px solid #cae8d6;border-radius:9px;background:#f1fbf5;color:#577365;font-size:12px;line-height:1.5}.safety-note .anticon{color:#25a45a}.payment-frame{min-height:510px;border:1px solid #e2e8f1;border-radius:9px;overflow:hidden;background:#fff}.payment-frame iframe{display:block;width:100%;height:510px;border:0;background:#fff}.modal-note{margin:12px 0 0;color:#73859b;font-size:12px}@media(max-width:1000px){.recharge-grid{grid-template-columns:1fr}}@media(max-width:640px){.recharge-page{padding:16px 12px 40px}.recharge-hero{flex-direction:column;align-items:stretch;padding:22px}.recharge-hero h1{font-size:25px}.balance-card{min-width:0}.recharge-card{padding:20px 16px}.amount-presets{grid-template-columns:repeat(2,minmax(0,1fr))}.payment-actions .ant-btn{flex:1 1 100%}.summary-rows>div{font-size:12px}}
</style>
